import {
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  ShareAltOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Space, Typography, Tooltip, Dropdown } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSessionStore } from "../../store/session-store";
import { useAlertStore } from "../../store/alert-store.js";
import { workflowService } from "../../components/workflows/workflow/workflow-service.js";
import { AppDeployment } from "./AppDeployment.jsx";
import { Layout } from "../../components/deployments/layout/Layout.jsx";
import { DeleteModal } from "../../components/deployments/delete-modal/DeleteModal.jsx";
import "./AppDeployments.css";
import { appDeploymentsService } from "./app-deployments-service.js";
import { NotFound } from "../../components/error/NotFound/NotFound.jsx";
import { useExceptionHandler } from "../../hooks/useExceptionHandler";

function AppDeployments() {
  const { sessionDetails } = useSessionStore();
  const handleException = useExceptionHandler();

  const { orgName, flags } = sessionDetails;
  const { setAlertDetails } = useAlertStore();
  const appDeploymentsApiService = appDeploymentsService();
  const navigate = useNavigate();
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [openAddAppModal, setOpenAddAppModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  // const [openShareModal, setOpenShareModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [tableData, setTableData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [workflowList, setWorkflowList] = useState([]);
  const workflowApiService = workflowService();

  useEffect(() => {
    getAppDeploymentList();
    getWorkflows();
  }, []);

  const getWorkflows = () => {
    workflowApiService
      .getWorkflowEndpointList("DESTINATION", "APPDEPLOYMENT")
      .then((res) => {
        setWorkflowList(res?.data);
      })
      .catch(() => {
        setAlertDetails({
          type: "error",
          content: "Unable to get workflow list.",
        });
      });
  };

  const getAppDeploymentList = () => {
    setIsTableLoading(true);

    appDeploymentsApiService
      .getAppDeploymentsList()
      .then((res) => {
        setTableData(res?.data);
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to update"));
      })
      .finally(() => {
        setIsTableLoading(false);
      });
  };

  const deleteappDeployment = () => {
    appDeploymentsApiService
      .deleteAppDeployment(selectedRow.id)
      .then((res) => {
        setOpenDeleteModal(false);
        getAppDeploymentList();
        setAlertDetails({
          type: "success",
          content: "App Deployment Deleted Successfully",
        });
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to update"));
      });
  };

  // Uncomment when enabling status update
  // const updateStatus = (record) => {
  //   setIsTableLoading(true);
  //   record.is_active = !record?.is_active;
  //   appDeploymentsApiService
  //     .updateAppDeployment(record)
  //     .then((res) => {
  //       setAlertDetails({
  //         type: "success",
  //         content: "Status updated successfully",
  //       });
  //     })
  //     .catch((err) => {
  //       setAlertDetails({
  //         type: "error",
  //         content: JSON.stringify(err?.response?.data?.detail),
  //       });
  //     })
  //     .finally(() => {
  //       setIsTableLoading(false);
  //     });
  // };

  // Uncomment when enabling edit
  const openEditModal = () => {
    setIsEdit(true);
    setOpenAddAppModal(true);
  };

  const openAddModal = () => {
    setIsEdit(false);
    setOpenAddAppModal(true);
  };

  const actionItems = [
    {
      key: "1",
      label: (
        <Space
          direction="horizontal"
          className="action-items"
          onClick={() => openEditModal(true)}
        >
          <div>
            <EditOutlined />
          </div>
          <div>
            <Typography.Text>Edit</Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      key: "2",
      label: (
        <Space
          direction="horizontal"
          className="action-items"
          onClick={() =>
            window.open(
              `${window.location.origin}/${orgName}/app/${selectedRow.app_name}`,
              "_blank"
            )
          }
        >
          <div>
            <ArrowRightOutlined />
          </div>
          <div>
            <Typography.Text>Go to App</Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      key: "3",
      label: (
        <Space
          direction="horizontal"
          className="action-items"
          // onClick={(event) => handleShareClick(event, item, true)}
        >
          <div>
            <ShareAltOutlined />
          </div>
          <div>
            <Typography.Text>Share</Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      key: "4",
      label: (
        <Space
          direction="horizontal"
          className="action-items"
          onClick={() => setOpenDeleteModal(true)}
        >
          <div>
            <DeleteOutlined />
          </div>
          <div>
            <Typography.Text>Delete</Typography.Text>
          </div>
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: "App Name",
      key: "application_name",
      render: (_, record) => (
        <Typography.Text strong>{record?.app_display_name}</Typography.Text>
      ),
      align: "left",
    },
    {
      title: "App Id",
      key: "app_id",
      render: (_, record) => <Space>{record?.app_name}</Space>,
      align: "left",
    },
    {
      title: "Description",
      key: "description",
      render: (_, record) => <Space>{record?.description}</Space>,
      align: "left",
    },
    {
      title: (
        <span>
          Workflow{" "}
          <Tooltip title="Currently, each app supports a maximum of 5 documents.">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      key: "workflow_name",
      render: (_, record) => (
        <Tooltip title="view workflow">
          <Space
            className="workflowName"
            onClick={() =>
              navigate(
                `/${sessionDetails?.orgName}/workflows/${record?.workflow}`
              )
            }
          >
            {record?.workflow_name}
          </Space>
        </Tooltip>
      ),
      align: "left",
    },
    {
      title: "Actions",
      key: "pipeline_id",
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{ items: actionItems }}
          placement="bottomLeft"
          onOpenChange={() => setSelectedRow(record)}
        >
          <EllipsisOutlined className="cur-pointer" />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      {flags.app_deployment ? (
        <>
          <Layout
            type="app"
            columns={columns}
            tableData={tableData}
            isTableLoading={isTableLoading}
            openAddModal={openAddModal}
          />
          {openAddAppModal && (
            <AppDeployment
              open={openAddAppModal}
              setOpen={setOpenAddAppModal}
              setTableData={setTableData}
              isEdit={isEdit}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              workflowList={workflowList}
            />
          )}
          <DeleteModal
            open={openDeleteModal}
            setOpen={setOpenDeleteModal}
            deleteRecord={deleteappDeployment}
          />
        </>
      ) : (
        <NotFound />
      )}
    </>
  );
}

export { AppDeployments };
